import { Platform } from 'react-native';
import { DIRECTION_META, EVENT_DIRECTIONS } from '../../constants/directions';

export type PracticeDirectionSlug =
  | 'meditation'
  | 'psychology'
  | 'philosophy'
  | 'body-practices'
  | 'breathwork'
  | 'morning-practice'
  | 'before-sleep'
  | 'traditions'
  | 'healthy-body'
  | 'music'
  | 'art'
  | 'yoga'
  | 'emotional-intelligence'
  | 'literature'
  | 'nutrition'
  | 'longevity'
  | 'short-practice';

type PracticeDirectionOption = {
  id: string;
  slug: PracticeDirectionSlug;
  nameRu: string;
  nameEn: string;
};

// CORE-339 is the canonical catalogue and ordering source. This module only
// adds the shareable hash contract used by the home intention links.
export const PRACTICE_DIRECTION_OPTIONS: readonly PracticeDirectionOption[] = EVENT_DIRECTIONS.map(
  nameRu => {
    const meta = DIRECTION_META[nameRu];
    return {
      id: nameRu,
      slug: (meta?.slug ?? encodeURIComponent(nameRu)) as PracticeDirectionSlug,
      nameRu,
      nameEn: meta?.nameEn ?? nameRu,
    };
  },
);

const PATH_ID_BY_SLUG: Readonly<Record<string, string>> = Object.fromEntries(
  PRACTICE_DIRECTION_OPTIONS.map(option => [option.slug, option.id]),
) as Record<string, string>;

// Preserve links shared before the descriptive practice URL slugs became canonical.
const LEGACY_PATH_ID_BY_SLUG: Readonly<Record<string, string>> = {
  body: 'Телесные практики',
  eq: 'Эмоциональный интеллект',
  morning: 'Утренняя практика',
  sleep: 'Перед сном',
  short: 'Короткая практика',
};

export function parsePracticeDirectionsFromHash(): string[] {
  if (Platform.OS !== 'web') return [];
  try {
    return parsePracticeDirectionsHash(window.location.hash);
  } catch {
    return [];
  }
}

/** Parse a raw URL fragment without depending on browser globals. */
export function parsePracticeDirectionsHash(hash: string): string[] {
  let raw = hash.replace(/^#/, '').trim();
  if (!raw) return [];

  try {
    raw = decodeURIComponent(raw);
  } catch {
    // A malformed value must not make the other, valid ASCII slugs disappear.
  }

  const ids: string[] = [];
  for (const value of raw.split(',')) {
    const slug = value.trim().toLowerCase();
    const id = PATH_ID_BY_SLUG[slug] ?? LEGACY_PATH_ID_BY_SLUG[slug];
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Subscribe to browser fragment changes and immediately replay the current
 * value. The initial replay is important when navigation writes the hash just
 * before Practices mounts: in that case the browser event has already fired.
 */
export function subscribeToPracticeDirectionsHash(
  onChange: (ids: string[]) => void,
): () => void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return () => {};

  const syncFromHash = () => onChange(parsePracticeDirectionsFromHash());
  window.addEventListener('hashchange', syncFromHash);
  syncFromHash();

  return () => window.removeEventListener('hashchange', syncFromHash);
}

export function practiceDirectionHashFromIds(ids: string[]): string {
  const slugs = PRACTICE_DIRECTION_OPTIONS
    .filter(option => ids.includes(option.id))
    .map(option => option.slug);
  return slugs.length ? `#${slugs.join(',')}` : '';
}

export function practiceDirectionsUrl(slugs: readonly PracticeDirectionSlug[]): string {
  const requested = new Set(slugs);
  const stableSlugs = PRACTICE_DIRECTION_OPTIONS
    .filter(option => requested.has(option.slug))
    .map(option => option.slug);
  return stableSlugs.length ? `/practices#${stableSlugs.join(',')}` : '/practices';
}
